import { useLogout } from "services/LogoutProvider";
import ApiError from "services/Errors/ApiError";
import { getUrls } from "utils/urls";
import { STORAGE_KEY } from "utils/strings";
import { USER_TYPE } from "utils/constants";

type FetchWithAuthOptions = RequestInit & {
  headers?: Record<string, string>;
};
interface UseApiWrapperReturn {
  fetchWithAuth: (url: string, options?: FetchWithAuthOptions) => Promise<any>;
}

const useApiWrapper = (): UseApiWrapperReturn => {
  const { handleSessionExpired, handleUnauthorized } = useLogout();
  const URLS = getUrls();

  const refreshAccessToken = async (): Promise<string | null> => {
    try {
      const response = await fetch(URLS.REFRESH_TOKEN, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ type: USER_TYPE }),
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      localStorage.setItem(STORAGE_KEY.API_ACCESS_TOKEN_DATA, data.accessToken);
      return data.accessToken;
    } catch {
      return null;
    }
  };

  const fetchWithAuth = async (
    url: string,
    options: FetchWithAuthOptions = {},
  ): Promise<any> => {
    try {
      let token = localStorage.getItem(STORAGE_KEY.API_ACCESS_TOKEN_DATA);
      let headers = {
        Authorization: `Bearer ${token}`,
        ...options.headers,
      };
      let response = await fetch(url, {
        ...options,
        headers,
        credentials: "include",
      });

      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        if (!newToken) {
          handleSessionExpired();
          return;
        }
        // Retry original request with new access token
        headers = { ...headers, Authorization: `Bearer ${newToken}` };
        response = await fetch(url, { ...options, headers });
      }

      if (response.status === 403) {
        handleUnauthorized();
        return;
      }
      if (response.status === 404) {
        const errorData = await response.json();
        const errorMessage =
          errorData.errorMessage || errorData.message || "No Data Found";
        throw new ApiError(response.status, errorMessage);
      }
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.errorMessage || errorData.message || "API request failed",
        );
      }
      // ✅ Decide response format based on content type
      const contentType = response.headers.get("content-type") || "";
      if (
        contentType.includes("application/json") ||
        contentType.includes("text/json")
      ) {
        return await response.json();
      } else {
        return await response.blob();
      }
    } catch (error: any) {
      throw error;
    }
  };

  return { fetchWithAuth };
};

export default useApiWrapper;
